import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Alert,
    Avatar,
    CircularProgress,
    Button,
} from '@mui/material';
import { MarkEmailRead, ErrorOutline } from '@mui/icons-material';
import api from '../../services/api';

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [status, setStatus] = useState('checking'); // checking | success | error
    const [message, setMessage] = useState('');
    // The token is single-use, so a duplicate call (React 18 StrictMode
    // double-invokes effects in development) must not re-hit the API --
    // the second call would see it already consumed and report a false
    // "invalid" error over a real success.
    const requested = useRef(false);

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setMessage('This activation link is missing its token.');
            return;
        }
        if (requested.current) return;
        requested.current = true;

        api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
            .then((res) => {
                setStatus('success');
                setMessage(res.data?.message || 'Account activated -- you can sign in now.');
            })
            .catch((err) => {
                setStatus('error');
                setMessage(err.response?.data?.message || 'This activation link is invalid or has expired.');
            });
        // Runs once for whatever token was in the URL on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        padding: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Avatar
                        sx={{
                            m: 1,
                            bgcolor: status === 'error' ? 'error.main' : 'primary.main',
                            width: 56,
                            height: 56,
                        }}
                    >
                        {status === 'error' ? <ErrorOutline fontSize="large" /> : <MarkEmailRead fontSize="large" />}
                    </Avatar>

                    <Typography component="h1" variant="h5" gutterBottom>
                        Account Activation
                    </Typography>

                    {status === 'checking' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="textSecondary">Verifying...</Typography>
                        </Box>
                    )}

                    {status === 'success' && (
                        <Alert severity="success" sx={{ width: '100%', mt: 2 }}>{message}</Alert>
                    )}

                    {status === 'error' && (
                        <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{message}</Alert>
                    )}

                    {status !== 'checking' && (
                        <Button
                            component={Link}
                            to="/login"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3 }}
                            size="large"
                        >
                            Go to Login
                        </Button>
                    )}
                </Paper>

                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    © {new Date().getFullYear()} Solidpro Mech. All rights reserved.
                </Typography>
            </Box>
        </Container>
    );
};

export default VerifyEmail;
